import { Contact, ContactInput, ContactsResponse, Category, CategoryInput, CategoriesResponse, ImportResult } from '../types';
import { InboxConversation, InboxConversationsResponse, InboxImportProgress, InboxMessage } from '../types/inbox';

const API_BASE_URL = '/api';

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('auth_token');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));

      // Detectar erro de quota e adicionar flag para tratamento especial
      if (error.upgradeRequired || (error.message && /\blimite?\b/i.test(error.message))) {
        const quotaError = new Error(error.message || error.error || 'Limit reached');
        (quotaError as any).isQuotaError = true;
        (quotaError as any).upgradeRequired = true;
        throw quotaError;
      }

      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  async getContacts(params?: {
    search?: string;
    tag?: string;
    page?: number;
    pageSize?: number;
  }): Promise<ContactsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/contatos${queryString ? `?${queryString}` : ''}`;

    return this.request<ContactsResponse>(endpoint);
  }

  async getContact(id: string): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`);
  }

  async createContact(data: ContactInput): Promise<Contact> {
    return this.request<Contact>('/contatos', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateContact(id: string, data: ContactInput): Promise<Contact> {
    return this.request<Contact>(`/contatos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(id: string): Promise<void> {
    await this.request<void>(`/contatos/${id}`, {
      method: 'DELETE',
    });
  }

  // Category methods
  async getCategories(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<CategoriesResponse> {
    const searchParams = new URLSearchParams();

    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

    const queryString = searchParams.toString();
    const endpoint = `/categorias${queryString ? `?${queryString}` : ''}`;

    return this.request<CategoriesResponse>(endpoint);
  }

  async getAllCategories(): Promise<Category[]> {
    return this.request<Category[]>('/categorias/all');
  }

  async getCategory(id: string): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`);
  }

  async createCategory(data: CategoryInput): Promise<Category> {
    return this.request<Category>('/categorias', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id: string, data: CategoryInput): Promise<Category> {
    return this.request<Category>(`/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    await this.request<void>(`/categorias/${id}`, {
      method: 'DELETE',
    });
  }

  // CSV Import methods
  async importCSV(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('csv', file);

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/import`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async exportContactsExcel(params?: { search?: string; tag?: string }): Promise<Blob> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.tag) searchParams.set('tag', params.tag);
    const queryString = searchParams.toString();

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/contatos/export${queryString ? `?${queryString}` : ''}`, {
      headers
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.blob();
  }

  async downloadCSVTemplate(): Promise<Blob> {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {};

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/csv/template`, {
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.blob();
  }

  // Bulk operations
  async post(endpoint: string, data: any): Promise<any> {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Chatwoot integration
  async getChatwootTags(): Promise<{ tags: Array<{ name: string; count: number }> }> {
    return this.request('/chatwoot/tags');
  }

  async syncChatwootContacts(tagMappings: Array<{ chatwootTag: string; categoryId: string }>): Promise<{ imported: number; updated: number }> {
    return this.request('/chatwoot/sync', {
      method: 'POST',
      body: JSON.stringify({ tagMappings }),
    });
  }

  // Perfex CRM Integration
  async getPerfexLeads(): Promise<{ success: boolean; leads: any[]; total: number }> {
    return this.request('/perfex/leads');
  }

  async importPerfexLeads(leadIds: string[], categoryId: string): Promise<{ success: boolean; imported: number; updated: number; errors: number }> {
    return this.request('/perfex/import', {
      method: 'POST',
      body: JSON.stringify({ leadIds, categoryId }),
    });
  }

  async getPerfexStatuses(): Promise<{ success: boolean; statuses: Array<{ id: string; name: string; color?: string }> }> {
    return this.request('/perfex/statuses');
  }

  async getPerfexSources(): Promise<{ success: boolean; sources: Array<{ id: string; name: string }> }> {
    return this.request('/perfex/sources');
  }

  async getPerfexStaff(): Promise<{ success: boolean; staff: Array<{ staffid: string; firstname: string; lastname: string; email: string }> }> {
    return this.request('/perfex/staff');
  }

  // Inbox - conversas e respostas
  async getInboxConversations(params?: {
    sessionId?: string;
    search?: string;
    filter?: 'all' | 'unread' | 'replies';
    limit?: number;
    offset?: number;
  }): Promise<InboxConversationsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.sessionId) searchParams.set('sessionId', params.sessionId);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.filter) searchParams.set('filter', params.filter);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    return this.request(`/inbox/conversations${query ? `?${query}` : ''}`);
  }

  async getInboxMessages(conversationId: string, params?: { limit?: number; before?: string }): Promise<{
    conversation: InboxConversation;
    messages: InboxMessage[];
    hasMore: boolean;
  }> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.before) searchParams.set('before', params.before);
    const query = searchParams.toString();
    return this.request(`/inbox/conversations/${conversationId}/messages${query ? `?${query}` : ''}`);
  }

  async markInboxConversationRead(conversationId: string): Promise<{ success: boolean }> {
    return this.request(`/inbox/conversations/${conversationId}/read`, { method: 'POST' });
  }

  async getInboxUnreadCount(): Promise<{ count: number }> {
    return this.request('/inbox/unread-count');
  }

  async sendInboxReply(conversationId: string, body: {
    text?: string;
    mediaUrl?: string;
    mediaType?: 'image' | 'video' | 'audio' | 'document';
    mediaMimeType?: string;
    fileName?: string;
    caption?: string;
  }): Promise<{ success: boolean; message: InboxMessage; conversation: InboxConversation }> {
    return this.request(`/inbox/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async startInboxImport(sessionId?: string): Promise<InboxImportProgress> {
    return this.request('/inbox/import', {
      method: 'POST',
      body: JSON.stringify(sessionId ? { sessionId } : {}),
    });
  }

  async getInboxImportStatus(sessionId?: string): Promise<InboxImportProgress> {
    return this.request(`/inbox/import/status${sessionId ? `?sessionId=${sessionId}` : ''}`);
  }

  async downloadInboxMedia(messageId: string): Promise<{ success: boolean; message: InboxMessage }> {
    return this.request(`/inbox/messages/${messageId}/media`, { method: 'POST' });
  }

  async setConnectionInbox(sessionName: string, enabled: boolean): Promise<{ success: boolean; inboxEnabled: boolean }> {
    return this.request(`/waha/sessions/${encodeURIComponent(sessionName)}/inbox`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  }
}

export const apiService = new ApiService();
export const api = apiService;